/**
 * @file PageDock.tsx
 * @brief The bottom dock for a page's main action, as on Boardrooms and Notes.
 *
 * @details
 * DES (01-10-2026), on "New user" in the Users header: "desktop app per is
 * kisim kay button hameesha neechay hoon jaisay doosray pages per hain". A
 * page's create action sits in a dock at the bottom centre, sticky above
 * the shell footer, not in the page header.
 *
 * The page's root must be a flex column at least the viewport tall
 * (`pageWithDockClass`), so `mt-auto` pins the dock to the bottom even
 * when the list is short.
 *
 * @author DES
 * @date 01-10-2026
 * @updated 08-10-2026 — shared dock fade behind the row; dark glass action.
 */

import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "@/shared/theme/cn";
import { dockFadeBehind, dockGlassDark } from "@/shared/theme/dockGlass";

/** Root class for a page that has a `PageDock`. */
export const pageWithDockClass = "flex min-h-[calc(100dvh-5rem)] flex-col";

export function PageDock({ children }: { children: ReactNode }) {
  return (
    // THE dock fade behind the row, both themes (DES 08-10-2026).
    <div
      className={cn(
        "pointer-events-none sticky bottom-6 z-20 mt-auto flex justify-center gap-3 pt-8",
        dockFadeBehind,
      )}
    >
      <div className="pointer-events-auto flex items-center gap-3">{children}</div>
    </div>
  );
}

/**
 * The dock's main action: the red 56px pill in light; in dark THE dock glass
 * with a brand-red glyph and a primary-text label (DES 08-10-2026).
 */
export function DockActionButton({
  icon,
  children,
  className,
  type = "button",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { icon?: ReactNode }) {
  return (
    <button
      type={type}
      className={cn(
        "flex h-14 items-center gap-2 rounded-full bg-brand px-8 text-base font-bold text-white shadow-[0_8px_30px_rgba(0,0,0,0.12)] transition-colors hover:bg-brand/90 disabled:opacity-60",
        dockGlassDark,
        "dark:text-app-text dark:[&>svg]:text-brand",
        className,
      )}
      {...props}
    >
      {icon}
      {children}
    </button>
  );
}
